import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { appConfig } from '../config/appConfig';
import { logError } from '../utils/logger';
import {
  ConciergeSearchRequest,
  ConciergeSearchResponse,
  IServiceQuery,
  SearchSuggestionsResponse,
  ServiceSuggestionsResponse,
} from '../utils/type';

const PRODUCT_PROFIX = appConfig.api.productServiceEndpoint;

export const sumbitServiceData = async (data: any) => {
  try {
    const res = await apiClient.post(
      `${PRODUCT_PROFIX}/${Path.updateService}`,
      data
    );
    return res.data;
  } catch (error: any) {
    logError('Error submitting service data:', error);
    throw error;
  }
};

export const getServiceDataById = async (id: string) => {
  try {
    const res = await apiClient.get(
      `${PRODUCT_PROFIX}/${Path.fetchPublicServiceById}` + `/${id}`
    );
    return res.data;
  } catch (error: any) {
    logError('Error fetching service data:', error);
    throw error;
  }
};

//GET PROVIDER SERVICE
export const getServiceDataByProvider = async ({
  page = 1,
  limit = 1,
  id: providerId,
}: any) => {
  try {
    const query = `?page=${page}&limit=${limit}`;
    const res = await apiClient.get(
      `${PRODUCT_PROFIX}/${Path.getPublicServiceByProviderId}/${providerId}${query}`
    );

    console.log('PAGINATION: ', res.data);
    return res.data;
  } catch (error: any) {
    logError('Error fetching service data by provider:', error);
    throw error;
  }
};

export const checkBookingAvailability = async (data: any) => {
  try {
    const res = await apiClient.post(
      `${PRODUCT_PROFIX}/${Path.checkBookingAvailability}`,
      data
    );
    return res.data;
  } catch (error: any) {
    logError('Error checking booking availability:', error);
    throw error;
  }
};

export const createNewConversation = async (
  senderId: string,
  receiverId: string,
  businessId: string
) => {
  try {
    const res = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_CHAT}/${Path.createNewConversation}`,
      {
        senderId,
        receiverId,
        senderRole: 'customer',
        receiverRole: 'provider',
        type: 'personal',
        businessId,
      }
    );
    return res.data;
  } catch (error: any) {
    logError('Error creating new conversation: ', error);
    throw error;
  }
};

export const getServiceSuggestions = async (
  searchTerm: string,
  categoryId?: string
): Promise<ServiceSuggestionsResponse> => {
  try {
    const params = new URLSearchParams({
      search: searchTerm,
      limit: '10',
    });

    if (categoryId && categoryId !== 'all' && categoryId !== '') {
      params.append('categoryId', categoryId);
    }

    const response = await apiClient.get(
      `${PRODUCT_PROFIX}/${Path.serviceSuggestions}?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    logError('Error fetching service suggestions:', error);
    return {
      success: false,
      suggestions: [],
    };
  }
};

export const getAllServiceByQuery = () => async (data: IServiceQuery) => {
  try {
    const formatQuery = {
      ...data,
      filters: data.filters ? JSON.stringify(data.filters) : undefined,
    };

    const res = await apiClient.get(
      `${PRODUCT_PROFIX}/${Path.allServicebyQuery}`,
      {
        params: formatQuery,
      }
    );

    return res.data.data;
  } catch (error: unknown) {
    console.error('Error get all service by query', error);
    throw error;
  }
};

export const getServiceByQuery = () => async (data: IServiceQuery) => {
  try {
    const formatQuery = {
      ...data,
      filters: data.filters ? JSON.stringify(data.filters) : undefined,
    };

    const res = await apiClient.get(
      `${PRODUCT_PROFIX}/${Path.servicebyQuery}`,
      {
        params: formatQuery,
      }
    );

    return res.data.data.service;
  } catch (error: unknown) {
    console.error('Error get service by query', error);
    throw error;
  }
};

// GET /api/v1/service/searchSuggestions — popular/trending queries for the
// concierge search suggestion dropdown.  Data is aggregated server-side and
// cached in Redis; this fetches the full top-N list once on input focus.
export const getSearchSuggestions =
  async (): Promise<SearchSuggestionsResponse> => {
    try {
      const response = await apiClient.get(
        `${PRODUCT_PROFIX}/${Path.searchSuggestions}`
      );
      return response.data;
    } catch (error: any) {
      logError('Error fetching search suggestions:', error);
      return { success: false, data: { popular: [] } };
    }
  };

// POST /api/v1/service/conciergeSearch — turns free text into AI-matched
// results for the landing page's "Ask Prowl" widget. The backend already
// fails soft (falls back to a literal keyword search server-side when
// hp-ai-service/AI_SERVICE_TOKEN is unavailable), so this always resolves to
// a real result set rather than an error, as long as the network call itself
// succeeds. Callers should still handle a thrown error (network down, 5xx)
// by falling back to the widget's own client-side matching.
export const getConciergeSearch = async (
  payload: ConciergeSearchRequest
): Promise<ConciergeSearchResponse> => {
  try {
    const res = await apiClient.post(
      `${PRODUCT_PROFIX}/${Path.conciergeSearch}`,
      payload
    );
    return res.data;
  } catch (error: any) {
    logError('Error fetching concierge search results:', error);
    throw error;
  }
};

export const getAllPublicServiceByProvider =
  () => async (data: IServiceQuery) => {
    try {
      const params: Record<string, any> = {};

      console.log('data', data);

      if (data.filters) params.filters = JSON.stringify(data.filters);
      if (data.isVisible !== undefined) params.isVisible = data.isVisible;
      if (data.isDeleted !== undefined) params.isDeleted = data.isDeleted;
      if (data.fullProfile) params.fullProfile = data.fullProfile;
      if (data.withMongoId) params.withMongoId = data.withMongoId;
      if (data.serviceProjection)
        params.serviceProjection = data.serviceProjection;
      if (data.categoryProjection)
        params.categoryProjection = data.categoryProjection;
      if (data.galleryProjection)
        params.galleryProjection = data.galleryProjection;
      if (data.businessProjection)
        params.businessProjection = data.businessProjection;
      if (data.locationProjection)
        params.locationProjection = data.locationProjection;
      if (data.page) params.page = data.page;
      if (data.limit) params.limit = data.limit;

      const res = await apiClient.get(
        `${PRODUCT_PROFIX}/${Path.allPublicServiceByProvider}`,
        { params }
      );

      return res.data.data;
    } catch (error: unknown) {
      console.error('Error get all service by business id', error);
      throw error;
    }
  };
